import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  Stack,
  Switch,
} from '@chakra-ui/react';
import { useFetchAfesPageQuery, useFetchUsersPageQuery } from '../../api';
import {
  AfeAutoComplete,
  AppInput,
  FormButtonGroup,
  UserAutoComplete,
} from '../components';
import { DatePicker } from '../form_fields';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PostWorkOrderRequest } from '../../api/bindings/PostWorkOrderRequest';
import { useAuth } from '../../core/hooks';

export interface WorkOrderCreateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  onSubmit: (form: PostWorkOrderRequest & { includeRevision: boolean }) => void;
  onClose: () => void;
}

interface Form extends PostWorkOrderRequest {
  includeRevision: boolean;
}

export const WorkOrderCreateFormDrawerContent = (
  props: WorkOrderCreateFormDrawerContentProps
) => {
  const { onSubmit, onClose, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: afes } = useFetchAfesPageQuery(
    { clientId: auth?.user.clientId ?? '0', all: true },
    { skip: !auth?.user.clientId }
  );
  const { data: users } = useFetchUsersPageQuery(
    { clientId: auth?.user.clientId ?? '0', all: true },
    { skip: !auth?.user.clientId }
  );

  // State
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      afeId: '0',
      assigneeUserId: '0',
      name: '',
      description: null,
      reference: null,
      commitmentDate: null,
      includeRevision: true,
    },
  });
  const { control, handleSubmit, formState, register, watch } = form;

  const woName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>
            Create New Work Order: {woName || 'Untitled'}
          </DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel htmlFor="afe">AFE:</FormLabel>
                <Controller
                  control={control}
                  name="afeId"
                  rules={{
                    required: 'AFE is required',
                    validate: (value) =>
                      BigInt(value) > 0 || 'Please select an AFE',
                  }}
                  render={(p) => (
                    <AfeAutoComplete
                      id="afe"
                      items={afes?.items}
                      {...p.field}
                    />
                  )}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    minLength: {
                      value: 3,
                      message: 'Description is between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Description is between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="reference">Reference:</FormLabel>
                <AppInput
                  id="reference"
                  {...register('reference', {
                    maxLength: {
                      value: 256,
                      message:
                        'Reference must be no greater than 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel htmlFor="asignee">Asignee:</FormLabel>
                <Controller
                  control={control}
                  name="assigneeUserId"
                  rules={{
                    required: 'Asignee is required',
                    validate: (value) =>
                      BigInt(value) > 0 || 'Please select an assignee',
                  }}
                  render={(p) => (
                    <UserAutoComplete
                      id="asignee"
                      items={users?.items}
                      {...p.field}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Commitment Date:</FormLabel>
                <DatePicker name="commitmentDate" control={control} />
              </FormControl>
              <FormControl>
                <FormLabel>Include a Revision?</FormLabel>
                <Controller
                  control={control}
                  name="includeRevision"
                  render={(p) => (
                    <Switch
                      size="lg"
                      colorScheme="blue"
                      isChecked={p.field.value}
                      onChange={() => p.field.onChange(!p.field.value)}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
